import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { EMAIL_PROVIDERS } from "@/types/email";
import { 
  Mail, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Server,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { cn } from "@/lib/utils";

interface EmailSetupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const EmailSetupDialog = ({ open, onOpenChange, onSuccess }: EmailSetupDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [detectedProvider, setDetectedProvider] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    email_address: "",
    display_name: "",
    password: "",
    smtp_host: "",
    smtp_port: 587,
    imap_host: "",
    imap_port: 993,
  });

  // Auto-detect provider when email changes
  useEffect(() => {
    const detectProvider = async () => {
      if (!formData.email_address.includes('@')) {
        setDetectedProvider(null);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('email-server-discovery', {
          body: { email: formData.email_address }
        });

        if (!error && data) {
          setDetectedProvider(data.provider);
          setFormData(prev => ({
            ...prev,
            smtp_host: data.smtp_host || '',
            smtp_port: data.smtp_port || 587,
            imap_host: data.imap_host || '',
            imap_port: data.imap_port || 993,
          }));
        }
      } catch (err) {
        console.log('Provider detection failed:', err);
      }
    };

    const timeout = setTimeout(detectProvider, 500);
    return () => clearTimeout(timeout);
  }, [formData.email_address]);

  const testConnection = async () => {
    if (!formData.email_address || !formData.password) {
      toast({
        title: "Fehlende Daten",
        description: "E-Mail-Adresse und Passwort sind erforderlich.",
        variant: "destructive",
      });
      return false;
    }

    setTestingConnection(true);
    setConnectionStatus('testing');

    try {
      const { data, error } = await supabase.functions.invoke('sync-emails', {
        body: { 
          testOnly: true,
          account: {
            email_address: formData.email_address,
            imap_host: formData.imap_host,
            imap_port: formData.imap_port,
            imap_username: formData.email_address,
            imap_password: formData.password,
            smtp_host: formData.smtp_host,
            smtp_port: formData.smtp_port,
            smtp_username: formData.email_address,
            smtp_password: formData.password,
          }
        }
      });

      if (error || !data?.success) {
        setConnectionStatus('error');
        toast({
          title: "Verbindung fehlgeschlagen",
          description: data?.error || "Server konnte nicht erreicht werden.",
          variant: "destructive",
        });
        return false;
      }

      setConnectionStatus('success');
      return true;
    } catch (err) {
      setConnectionStatus('error');
      return false;
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Test connection first
    const connected = await testConnection();
    if (!connected) return;

    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('Keine Organisation zugeordnet');
      }

      const { error } = await supabase
        .from('email_accounts')
        .insert({
          user_id: user.id,
          organization_id: profile.organization_id,
          email_address: formData.email_address,
          display_name: formData.display_name || formData.email_address.split('@')[0],
          provider: 'custom',
          smtp_host: formData.smtp_host,
          smtp_port: formData.smtp_port,
          smtp_username: formData.email_address,
          smtp_password: formData.password,
          imap_host: formData.imap_host,
          imap_port: formData.imap_port,
          imap_username: formData.email_address,
          imap_password: formData.password,
          is_active: true,
          is_default: true,
        });

      if (error) throw error;

      toast({
        title: "Erfolgreich eingerichtet",
        description: "Ihr E-Mail-Konto wurde erfolgreich hinzugefügt.",
      });

      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      console.error('Setup error:', err);
      toast({
        title: "Fehler",
        description: "E-Mail-Konto konnte nicht eingerichtet werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            E-Mail-Konto einrichten
          </DialogTitle>
          <DialogDescription>
            Verbinden Sie Ihr E-Mail-Konto, um E-Mails zu senden und zu empfangen.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Address */}
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail-Adresse</Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={formData.email_address}
                onChange={(e) => setFormData(prev => ({ ...prev, email_address: e.target.value }))}
                placeholder="ihre@email.de"
                required
              />
              {detectedProvider && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle className="h-3 w-3" />
                  {detectedProvider}
                </div>
              )}
            </div>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Anzeigename (optional)</Label>
            <Input
              id="displayName"
              value={formData.display_name}
              onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
              placeholder="Max Mustermann"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Passwort / App-Passwort</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Bei Gmail/Outlook benötigen Sie ein App-Passwort
            </p>
          </div>

          {/* Advanced Settings Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Server className="h-4 w-4" />
            Erweiterte Einstellungen
            {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {/* Advanced Settings */}
          {showAdvanced && (
            <Card className="bg-muted/50">
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">IMAP Server</Label>
                    <Input
                      value={formData.imap_host}
                      onChange={(e) => setFormData(prev => ({ ...prev, imap_host: e.target.value }))}
                      placeholder="imap.provider.de"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">IMAP Port</Label>
                    <Input
                      type="number"
                      value={formData.imap_port}
                      onChange={(e) => setFormData(prev => ({ ...prev, imap_port: parseInt(e.target.value) }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">SMTP Server</Label>
                    <Input
                      value={formData.smtp_host}
                      onChange={(e) => setFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
                      placeholder="smtp.provider.de"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">SMTP Port</Label>
                    <Input
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Connection Status */}
          {connectionStatus !== 'idle' && (
            <div className={cn(
              "flex items-center gap-2 p-3 rounded-lg text-sm",
              connectionStatus === 'testing' && "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
              connectionStatus === 'success' && "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300",
              connectionStatus === 'error' && "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300"
            )}>
              {connectionStatus === 'testing' && <Loader2 className="h-4 w-4 animate-spin" />}
              {connectionStatus === 'success' && <CheckCircle className="h-4 w-4" />}
              {connectionStatus === 'error' && <AlertCircle className="h-4 w-4" />}
              {connectionStatus === 'testing' && "Verbindung wird getestet..."}
              {connectionStatus === 'success' && "Verbindung erfolgreich!"}
              {connectionStatus === 'error' && "Verbindung fehlgeschlagen"}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={loading || testingConnection}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              {loading || testingConnection ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Verbinden"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSetupDialog;
